/**
 * /me/payments/deductions — Session 3d PR 10.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { DeductionsBreakdownItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyDeductionsBreakdownPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-deductions-breakdown", employeeId],
    queryFn: () => session3dApi.listDeductionsBreakdown(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<DeductionsBreakdownItem>[] = [
    {
      headerKey: "me.deductions-breakdown.col.code",
      label: "Code",
      render: (r) => <span className="font-mono text-xs">{r.code}</span>,
    },
    {
      headerKey: "me.deductions-breakdown.col.label",
      label: "Label",
      render: (r) => <span className="font-medium">{r.label}</span>,
    },
    {
      headerKey: "me.deductions-breakdown.col.amount",
      label: "Amount (SAR)",
      render: (r) => <span className="font-mono">{r.amount_sar.toFixed(2)}</span>,
      traceText: (r) => r.trace_text,
    },
    {
      headerKey: "me.deductions-breakdown.col.pct",
      label: "% of gross",
      render: (r) => <span className="font-mono text-xs">{r.pct_of_gross.toFixed(1)}%</span>,
    },
  ];

  return (
    <MeListPage<DeductionsBreakdownItem>
      title={t("me.deductions-breakdown.title", "My Deductions Breakdown")}
      subtitle={t("me.deductions-breakdown.subtitle", "Where is your salary going? Detailed per-deduction visibility.")}
      domain="deductions-breakdown"
      khidmatPrefill={t("me.deductions-breakdown.ask_khidmat", "Explain my deductions for this period.")}
      query={query}
      columns={columns}
      rowKey={(r) => r.code}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.deductions-breakdown.title", "My Deductions Breakdown"),
        window_label: t("me.deductions-breakdown.window", "current period"),
      }}
      emptyHeadline={t("me.deductions-breakdown.empty_headline", "No deductions on your latest payslip.")}
      emptyBody={t("me.deductions-breakdown.empty_body", "Khidmat: nice — looks like a clean period.")}
    />
  );
}
